import { formatCustomerLastActivity } from "./customerMappers";

export const AGENT_PRESENCE_OPTIONS = [
  { value: "accepting", label: "Accepting", dot: "#52C41A" },
  { value: "notAccepting", label: "Not Accepting", dot: "#FF4D4F" },
  { value: "offline", label: "Offline", dot: "#93A1B0" },
  { value: "invitationSent", label: "Invitation sent", dot: "#93A1B0" },
  { value: "suspended", label: "Suspended", dot: "#FAAD14" },
];

export function normalizeCoUserRecord(item) {
  if (!item) return null;
  return item.user || item;
}

export function getAgentRecordId(user) {
  if (!user) return null;
  return user.id || user._id || null;
}

export function mapAgentGroupLabels(groups = []) {
  return (groups || [])
    .map((g) => g.title || g.name || g.group_name)
    .filter(Boolean);
}

export function formatAgentLastActivity(user) {
  const iso =
    user?.last_activity ||
    user?.last_seen ||
    user?.updatedAt ||
    user?.updated_at ||
    user?.joining_date ||
    null;
  return formatCustomerLastActivity(iso);
}

export function formatKbRoleLabel(kbRole) {
  const role = kbRole?.role || kbRole;
  if (!role) return null;
  const r = String(role).toLowerCase();
  if (r === "contributor") return "Contributor";
  if (r === "editor") return "Editor";
  if (r === "viewer") return "Viewer";
  if (r === "admin") return "Admin";
  return role.charAt(0).toUpperCase() + role.slice(1);
}

export function mapAgentTeamLabels(teams = []) {
  return (teams || []).map((t) => t.name || t.title).filter(Boolean);
}

export function mapAgentTeamIds(teams = []) {
  return (teams || []).map((t) => t.id || t._id).filter(Boolean);
}

export function mapPresenceToApiStatus(presence) {
  if (presence === "notAccepting") return "not_accepting";
  if (presence === "invitationSent") return "invitation_sent";
  return presence;
}

export function formatAgentRoleLabel(roleName) {
  const r = (roleName || "").toLowerCase();
  if (r === "admin_with_billing") return "Super Admin";
  if (r === "admin") return "Admin";
  if (r === "agent") return "Agent";
  if (r === "supervisor") return "Supervisor";
  if (!roleName) return "—";
  return roleName.charAt(0).toUpperCase() + roleName.slice(1).replace(/_/g, " ");
}

/** Normalize co-user `status` for comparisons (Accepting, not_accepting, Not Accepting, etc.). */
export function normalizeAgentStatusText(status) {
  return String(status || "")
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
}

export function isAgentSuspended(user) {
  if (!user) return false;
  const statusText = normalizeAgentStatusText(user.status);
  return Boolean(user.is_suspended || user.suspended || statusText === "suspended");
}

/**
 * Map co-user API payload to UI presence.
 * Prefer the `status` field from co-users detail/list when present.
 */
export function mapApiAgentPresence(user) {
  if (!user) return "offline";
  if (isAgentSuspended(user)) return "suspended";
  if (user.is_joined === false) return "invitationSent";

  const statusText = normalizeAgentStatusText(user.status);
  if (statusText) {
    if (statusText === "accepting" || statusText === "acceptingchat") return "accepting";
    if (statusText === "notaccepting" || statusText === "notacceptingchat") {
      return "notAccepting";
    }
    if (statusText === "offline") return "offline";
    if (statusText === "invitationsent") return "invitationSent";
    if (statusText === "suspended") return "suspended";
  }

  // Legacy payloads without a usable status field
  if (user.is_offline) return "offline";
  if (user.accept_chat) return "accepting";
  return "notAccepting";
}

export function formatAgentLastActivityDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export function mapApiAgentToTableRow(item) {
  const user = normalizeCoUserRecord(item);
  if (!user) return null;

  const groups = user.groups || [];
  const groupIds = groups.map((g) => g.id || g._id).filter(Boolean);
  const groupNames = mapAgentGroupLabels(groups);
  const teams = user.teams || (user.team ? [user.team] : []);
  const teamNames = mapAgentTeamLabels(teams);
  const teamIds = mapAgentTeamIds(teams);
  const team =
    user.teams_display ||
    user.team_name ||
    user.team?.name ||
    (teamNames.length > 1 ? `${teamNames[0]} +${teamNames.length - 1}` : teamNames[0]) ||
    "—";

  return {
    id: user.id || user._id,
    name: user.full_name || user.name || "",
    email: user.email || "",
    jobTitle: user.job_title || user.title || "",
    role: formatAgentRoleLabel(user.role_name),
    roleName: user.role_name || "",
    chats: user.chat_count ?? user.conversations_count ?? 0,
    group: groupNames[0] || user.primary_group?.title || user.primary_group?.name || "—",
    groupIds,
    team,
    teamIds,
    teamId: teamIds[0] || user.team?.id || user.team_id || null,
    presence: mapApiAgentPresence(user),
    lastActivity: formatAgentLastActivity(user),
    avatar: user.agent_image || user.profile_picture || null,
    isSuspended: isAgentSuspended(user),
    isJoined: user.is_joined !== false,
    raw: user,
  };
}

export function mapApiAgentToDetailView(user) {
  const u = normalizeCoUserRecord(user);
  if (!u) return null;

  const groups = mapAgentGroupLabels(u.groups);
  const teams = mapAgentTeamLabels(u.teams);
  const teamIds = mapAgentTeamIds(u.teams);
  const kbRole = u.kbRole?.role || u.kb_role || null;
  const kbLabel = formatKbRoleLabel(kbRole);
  const kbPermissions = kbLabel ? [kbLabel] : [];

  const presence = mapApiAgentPresence(u);
  const presenceLabel =
    AGENT_PRESENCE_OPTIONS.find((o) => o.value === presence)?.label || "—";

  return {
    id: getAgentRecordId(u),
    name: u.full_name || u.name || "",
    email: u.email || "",
    jobTitle: u.job_title || u.title || formatAgentRoleLabel(u.role_name) || "",
    company: u.company_name || u.company || "",
    role: formatAgentRoleLabel(u.role_name),
    roleName: u.role_name || "",
    presence,
    presenceLabel,
    isSuspended: isAgentSuspended(u),
    lastActivity: formatAgentLastActivity(u),
    lastActivityDate: formatAgentLastActivityDate(u.last_activity || u.last_seen || u.updatedAt || null),
    chats: u.chat_count ?? u.conversations_count ?? 0,
    chatLimit: u.agent_chat_limit != null && u.agent_chat_limit !== "" ? u.agent_chat_limit : "—",
    timezone: u.timezone || "—",
    team:
      u.teams_display ||
      u.team_name ||
      u.team?.name ||
      u.team?.title ||
      (teams.length > 1 ? `${teams[0]} +${teams.length - 1}` : teams[0]) ||
      "",
    teams,
    teamIds,
    teamId: teamIds[0] || u.team?.id || u.team?._id || u.team_id || null,
    groups,
    groupIds: (u.groups || []).map((g) => g.id || g._id).filter(Boolean),
    kbRole,
    kbPermissions,
    isActive: u.is_active !== false,
    isJoined: Boolean(u.is_joined),
    avatar: u.agent_image || u.profile_picture || null,
    raw: u,
  };
}

export function tabToAgentListStatus(tab) {
  if (!tab || tab === "all") return "all";
  return tab;
}

export function lastActivityParamToDays(value) {
  const map = {
    minutes: 1,
    hours: 1,
    week: 7,
    month: 30,
    "7": 7,
    "30": 30,
    "14": 14,
  };
  return map[value] || (value ? Number(value) : undefined);
}

export function computeAgentTabCounts(tabTotals = {}) {
  return {
    all: tabTotals.all ?? tabTotals[""] ?? 0,
    online: tabTotals.online ?? 0,
    offline: tabTotals.offline ?? 0,
    suspended: tabTotals.suspended ?? 0,
  };
}

export function buildAgentInvitePayload({ email, roleName, groupIds, teamIds, timezone }) {
  const payload = {
    email: Array.isArray(email) ? email : [email].filter(Boolean),
    role_name: roleName,
  };
  if (groupIds?.length) payload.agent_group_id = groupIds;
  if (teamIds?.length) payload.team_ids = teamIds;
  if (timezone) payload.timezone = timezone;
  return payload;
}

export function buildAgentBulkBody(action, agentIds, extra = {}) {
  return { action, agent_ids: agentIds, ...extra };
}

function idArraysEqual(a = [], b = []) {
  const left = [...a].map(String).sort();
  const right = [...b].map(String).sort();
  if (left.length !== right.length) return false;
  return left.every((value, index) => value === right[index]);
}

function buildAgentUpdatePayloadFull(form, { includeRole = true } = {}) {
  const payload = {
    name: form.name?.trim() || "",
    email: form.email?.trim() || "",
    job_title: form.jobTitle?.trim() || "",
    status: mapPresenceToApiStatus(form.presence),
    agent_chat_limit: String(form.chatLimit ?? "5"),
    timezone:
      form.timezone && form.timezone !== "—"
        ? form.timezone
        : "America/Los_Angeles",
    group_ids: form.groupIds || [],
    team_ids: form.teamIds || [],
  };
  if (includeRole && form.roleName && form.roleName !== "admin_with_billing") {
    payload.role_name = form.roleName;
  }
  if (form.kbRole) payload.kb_role = form.kbRole;
  return payload;
}

export function buildAgentUpdatePayload(form, { initialForm, includeRole = true } = {}) {
  if (!initialForm) {
    return buildAgentUpdatePayloadFull(form, { includeRole });
  }

  const payload = {};
  const name = form.name?.trim() || "";
  const initialName = initialForm.name?.trim() || "";
  if (name !== initialName) payload.name = name;

  const email = form.email?.trim() || "";
  const initialEmail = initialForm.email?.trim() || "";
  if (email !== initialEmail) payload.email = email;

  const jobTitle = form.jobTitle?.trim() || "";
  const initialJobTitle = initialForm.jobTitle?.trim() || "";
  if (jobTitle !== initialJobTitle) payload.job_title = jobTitle;

  const status = mapPresenceToApiStatus(form.presence);
  const initialStatus = mapPresenceToApiStatus(initialForm.presence);
  if (status !== initialStatus) payload.status = status;

  const chatLimit = String(form.chatLimit ?? "5");
  const initialChatLimit = String(initialForm.chatLimit ?? "5");
  if (chatLimit !== initialChatLimit) payload.agent_chat_limit = chatLimit;

  const timezone =
    form.timezone && form.timezone !== "—"
      ? form.timezone
      : "America/Los_Angeles";
  const initialTimezone =
    initialForm.timezone && initialForm.timezone !== "—"
      ? initialForm.timezone
      : "America/Los_Angeles";
  if (timezone !== initialTimezone) payload.timezone = timezone;

  const groupIds = form.groupIds || [];
  const initialGroupIds = initialForm.groupIds || [];
  if (!idArraysEqual(groupIds, initialGroupIds)) payload.group_ids = groupIds;

  const teamIds = form.teamIds || [];
  const initialTeamIds = initialForm.teamIds || [];
  if (!idArraysEqual(teamIds, initialTeamIds)) payload.team_ids = teamIds;

  if (includeRole && form.roleName && form.roleName !== "admin_with_billing") {
    const initialRole = initialForm.roleName || "";
    if (form.roleName !== initialRole) payload.role_name = form.roleName;
  }

  const kbRole = form.kbRole || "";
  const initialKbRole = initialForm.kbRole || "";
  if (kbRole !== initialKbRole) {
    if (kbRole) payload.kb_role = kbRole;
  }

  return payload;
}

/** Partial FormData for profile self-update via co-users/update (never includes role_name). */
export function buildCoUserProfileUpdateFormData({
  userName,
  userEmail,
  userPassword,
  image,
  chatLimit,
  jobTitle,
  initialProfile = {},
}) {
  const formData = new FormData();
  let hasChanges = false;

  if (userName !== (initialProfile.userName || "")) {
    formData.append("full_name", userName);
    hasChanges = true;
  }

  if (userEmail && userEmail !== (initialProfile.userEmail || "")) {
    formData.append("email", userEmail);
    hasChanges = true;
  }

  if (userPassword) {
    formData.append("password", userPassword);
    hasChanges = true;
  }

  if (image) {
    formData.append("agent_image", image);
    hasChanges = true;
  }

  if (Number(chatLimit) !== Number(initialProfile.chatLimit ?? 0)) {
    formData.append("agent_chat_limit", chatLimit);
    hasChanges = true;
  }

  const nextJobTitle = jobTitle?.trim() || "";
  const initialJobTitle = initialProfile.jobTitle?.trim() || "";
  if (nextJobTitle !== initialJobTitle) {
    formData.append("job_title", nextJobTitle);
    hasChanges = true;
  }

  return { formData, hasChanges };
}

export function isCoUserApiSuccess(payload) {
  return payload?.status === 200;
}
